import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Tab, Tabs, Button, CircularProgress,
  Alert, Chip, IconButton, Tooltip, Collapse, Snackbar,
  FormControl, InputLabel, Select, MenuItem, Paper,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, TableSortLabel, useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ClearIcon from '@mui/icons-material/Clear';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { alpha } from '@mui/material/styles';
import { smartWarehouseService } from '../services/smartWarehouseService';
import { equipmentService } from '../services/equipmentService';
import { cargoService, type CargoSettings, type CreateCargoSettingsRequest, type UpdateCargoSettingsRequest } from '../services/cargoService';
import CartModal from '../components/CartModal';
import ShelfModal from '../components/ShelfModal';
import BarcodePrintButton from '../components/BarcodePrintButton';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../../accounting/shared/StatsCard';
import GuideButton from '../../common/guide/GuideButton';

interface Warehouse {
  id: string;
  name: string;
}

interface Cart {
  id: string;
  warehouseId: string;
  cartCode: string;
  cartName: string;
  cartType?: string;
  barcodeData?: string;
  status: string;
  currentWaveId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

interface Slot {
  id: string;
  slotCode: string;
  barcodeData?: string;
  currentOrderId?: string;
  currentOrderNumber?: string;
  status: string;
  isActive: boolean;
}

interface Shelf {
  id: string;
  warehouseId: string;
  shelfCode: string;
  shelfName: string;
  totalSlots: number;
  barcodeData?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  usedSlots: number;
  slots?: Slot[];
}

type SortDirection = 'asc' | 'desc';
type CartSortField = 'cartCode' | 'cartName' | 'cartType' | 'status' | 'isActive';
type ShelfSortField = 'shelfCode' | 'shelfName' | 'totalSlots' | 'usedSlots' | 'isActive';
type CargoSortField = 'provider' | 'isDefault' | 'senderName' | 'isActive' | 'hasApiKey';

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  Available: 'success',
  InUse: 'warning',
  Packing: 'warning',
  Maintenance: 'error',
  Empty: 'default',
  Ready: 'success',
};

const STATUS_LABELS: Record<string, string> = {
  Available: 'Müsait',
  InUse: 'Kullanımda',
  Packing: 'Paketlemede',
  Maintenance: 'Bakımda',
  Empty: 'Boş',
  Ready: 'Hazır',
};

const STATUS_CHIP_SX = {
  width: 96,
  height: 22,
  justifyContent: 'center',
  '& .MuiChip-label': {
    width: '100%',
    textAlign: 'center',
    px: 1,
    fontSize: '0.72rem',
    fontWeight: 600,
  },
};

const TABLE_HEADER_SX = {
  '& .MuiTableCell-root': {
    bgcolor: 'primary.main',
    color: 'common.white',
    fontWeight: 700,
    fontSize: '0.78rem',
    borderRight: '1px solid rgba(255,255,255,0.15)',
    py: 1,
    px: 1.5,
    '&:last-of-type': {
      borderRight: 'none',
    },
  },
};

const WmsEquipmentManagement: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cartSortField, setCartSortField] = useState<CartSortField>('cartCode');
  const [cartSortDir, setCartSortDir] = useState<SortDirection>('asc');
  const [shelfSortField, setShelfSortField] = useState<ShelfSortField>('shelfCode');
  const [shelfSortDir, setShelfSortDir] = useState<SortDirection>('asc');
  const [cargoSortField, setCargoSortField] = useState<CargoSortField>('provider');
  const [cargoSortDir, setCargoSortDir] = useState<SortDirection>('asc');

  // Carts
  const [carts, setCarts] = useState<Cart[]>([]);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [editCart, setEditCart] = useState<Cart | null>(null);
  // Her ikisi / Tekli / Çoklu şema ayrımı
  const [cartScheme, setCartScheme] = useState<'Both' | 'Single' | 'Multi'>('Both');

  // Shelves
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [shelfModalOpen, setShelfModalOpen] = useState(false);
  const [editShelf, setEditShelf] = useState<Shelf | null>(null);
  const [expandedShelfId, setExpandedShelfId] = useState<string | null>(null);
  const [shelfSlots, setShelfSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Cargo settings
  const [cargoSettings, setCargoSettings] = useState<CargoSettings[]>([]);
  const [cargoModalOpen, setCargoModalOpen] = useState(false);
  const [editCargo, setEditCargo] = useState<CargoSettings | null>(null);
  const [cargoProviders, setCargoProviders] = useState<string[]>([]);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'cart' | 'shelf' | 'cargo'; id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Load warehouses and providers
  useEffect(() => {
    const load = async () => {
      try {
        const list = await smartWarehouseService.getAllList();
        const wh = list.map((w) => ({ id: w.id, name: w.warehouseName }));
        setWarehouses(wh);
        if (wh.length > 0 && !selectedWarehouseId) setSelectedWarehouseId(wh[0].id);
      } catch { /* ignore */ }
      try {
        const providers = await cargoService.getProviders();
        setCargoProviders(providers);
      } catch { setCargoProviders(['Yurtici', 'Aras', 'MNG', 'Surat', 'PTT']); }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load carts/shelves when warehouse changes
  const loadData = useCallback(async () => {
    if (!selectedWarehouseId) return;
    setLoading(true);
    try {
      const [cartsResp, shelvesResp] = await Promise.all([
        equipmentService.getCarts(selectedWarehouseId),
        equipmentService.getShelves(selectedWarehouseId),
      ]);
      const cartsData = (cartsResp.data?.Data || cartsResp.data?.data || []).map((c: any) => ({
        id: c.Id || c.id,
        warehouseId: c.WarehouseId || c.warehouseId,
        cartCode: c.CartCode || c.cartCode,
        cartName: c.CartName || c.cartName,
        cartType: c.CartType || c.cartType || '',
        barcodeData: c.BarcodeData || c.barcodeData,
        status: c.Status || c.status || 'Available',
        currentWaveId: c.CurrentWaveId || c.currentWaveId,
        isActive: c.IsActive ?? c.isActive ?? true,
        createdAt: c.CreatedAt || c.createdAt,
        updatedAt: c.UpdatedAt || c.updatedAt,
      }));
      setCarts(cartsData);

      // Load cargo settings
      try {
        const cg = await cargoService.getSettings(selectedWarehouseId);
        setCargoSettings(cg);
      } catch { setCargoSettings([]); }

      const shelvesData = (shelvesResp.data?.Data || shelvesResp.data?.data || []).map((s: any) => ({
        id: s.Id || s.id,
        warehouseId: s.WarehouseId || s.warehouseId,
        shelfCode: s.ShelfCode || s.shelfCode,
        shelfName: s.ShelfName || s.shelfName,
        totalSlots: s.TotalSlots ?? s.totalSlots ?? 0,
        barcodeData: s.BarcodeData || s.barcodeData,
        isActive: s.IsActive ?? s.isActive ?? true,
        createdAt: s.CreatedAt || s.createdAt,
        updatedAt: s.UpdatedAt || s.updatedAt,
        usedSlots: s.UsedSlots ?? s.usedSlots ?? 0,
      }));
      setShelves(shelvesData);
    } catch {
      showSnackbar('Veriler yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouseId, showSnackbar]);

  useEffect(() => { loadData(); }, [loadData]);

  // Cart CRUD
  const handleSaveCart = useCallback(async (data: { id?: string; cartCode: string; cartName: string; cartType?: string; status: string; isActive: boolean }) => {
    if (data.id) {
      await equipmentService.updateCart({
        Id: data.id, CartCode: data.cartCode, CartName: data.cartName,
        CartType: data.cartType || undefined,
        Status: data.status, IsActive: data.isActive,
      });
      showSnackbar('Araba güncellendi');
    } else {
      await equipmentService.createCart({
        WarehouseId: selectedWarehouseId, CartCode: data.cartCode, CartName: data.cartName,
        CartType: data.cartType || undefined,
      });
      showSnackbar('Araba oluşturuldu');
    }
    loadData();
  }, [selectedWarehouseId, loadData, showSnackbar]);

  const cartTypeLabel = (type?: string) => {
    if (type === 'Single') return 'Tekli';
    if (type === 'Multi') return 'Çoklu';
    return 'Her ikisi';
  };

  // Shelf CRUD
  const handleSaveShelf = useCallback(async (data: { id?: string; shelfCode: string; shelfName: string; totalSlots: number; isActive: boolean }) => {
    if (data.id) {
      await equipmentService.updateShelf({
        Id: data.id, ShelfCode: data.shelfCode, ShelfName: data.shelfName, IsActive: data.isActive,
      });
      showSnackbar('Raf güncellendi');
    } else {
      await equipmentService.createShelf({
        WarehouseId: selectedWarehouseId, ShelfCode: data.shelfCode,
        ShelfName: data.shelfName, TotalSlots: data.totalSlots,
      });
      showSnackbar('Raf ve bölmeleri oluşturuldu');
    }
    loadData();
  }, [selectedWarehouseId, loadData, showSnackbar]);

  // Delete
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'cart') {
        await equipmentService.deleteCart(deleteTarget.id);
        showSnackbar('Araba silindi');
      } else if (deleteTarget.type === 'shelf') {
        await equipmentService.deleteShelf(deleteTarget.id);
        showSnackbar('Raf ve bölmeleri silindi');
      } else if (deleteTarget.type === 'cargo') {
        await cargoService.deleteSettings(deleteTarget.id);
        showSnackbar('Kargo ayarı silindi');
      }
      loadData();
    } catch (err: any) {
      showSnackbar(err?.response?.data?.Message || err?.response?.data?.message || 'Silme hatası', 'error');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, loadData, showSnackbar]);

  // Cargo test connection
  const handleTestConnection = useCallback(async (id: string) => {
    setTestingConnection(id);
    try {
      const result = await cargoService.testConnection(id);
      showSnackbar(result.message, result.success ? 'success' : 'error');
    } catch {
      showSnackbar('Bağlantı testi başarısız', 'error');
    } finally {
      setTestingConnection(null);
    }
  }, [showSnackbar]);

  // Expand shelf to show slots
  const handleToggleShelf = useCallback(async (shelfId: string) => {
    if (expandedShelfId === shelfId) {
      setExpandedShelfId(null);
      setShelfSlots([]);
      return;
    }
    setExpandedShelfId(shelfId);
    setLoadingSlots(true);
    try {
      const resp = await equipmentService.getShelfDetail(shelfId);
      const data = resp.data?.Data || resp.data?.data;
      const slots = (data?.Slots || data?.slots || []).map((sl: any) => ({
        id: sl.Id || sl.id,
        slotCode: sl.SlotCode || sl.slotCode,
        barcodeData: sl.BarcodeData || sl.barcodeData,
        currentOrderId: sl.CurrentOrderId || sl.currentOrderId,
        currentOrderNumber: sl.CurrentOrderNumber || sl.currentOrderNumber,
        status: sl.Status || sl.status || 'Empty',
        isActive: sl.IsActive ?? sl.isActive ?? true,
      }));
      setShelfSlots(slots);
    } catch {
      showSnackbar('Bölme verileri yüklenemedi', 'error');
    } finally {
      setLoadingSlots(false);
    }
  }, [expandedShelfId, showSnackbar]);

  // Clear slot
  const handleClearSlot = useCallback(async (slotId: string) => {
    try {
      await equipmentService.clearSlot(slotId);
      showSnackbar('Bölme temizlendi');
      if (expandedShelfId) handleToggleShelf(expandedShelfId);
    } catch {
      showSnackbar('Bölme temizlenirken hata oluştu', 'error');
    }
  }, [expandedShelfId, handleToggleShelf, showSnackbar]);

  const sortBy = <T,>(arr: T[], dir: SortDirection, picker: (item: T) => string | number | boolean) => {
    const sorted = [...arr].sort((a, b) => {
      const av = picker(a);
      const bv = picker(b);
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      if (typeof av === 'boolean' && typeof bv === 'boolean') return Number(av) - Number(bv);
      return String(av ?? '').localeCompare(String(bv ?? ''), 'tr', { sensitivity: 'base' });
    });
    return dir === 'asc' ? sorted : sorted.reverse();
  };

  const sortedCarts = useMemo(() => {
    return sortBy(carts, cartSortDir, (c) => {
      if (cartSortField === 'isActive') return c.isActive;
      if (cartSortField === 'cartType') return c.cartType || '';
      if (cartSortField === 'status') return c.status || '';
      if (cartSortField === 'cartName') return c.cartName || '';
      return c.cartCode || '';
    });
  }, [carts, cartSortDir, cartSortField]);

  // "Her ikisi" (boş tip) arabalar Tekli ve Çoklu şemasında da görünür
  const cartMatchesScheme = useCallback((cart: Cart, scheme: 'Both' | 'Single' | 'Multi') => {
    const type = cart.cartType || '';
    if (scheme === 'Both') return type === '';
    if (type === '') return true;
    return type === scheme;
  }, []);

  const filteredCarts = useMemo(
    () => sortedCarts.filter((c) => cartMatchesScheme(c, cartScheme)),
    [sortedCarts, cartScheme, cartMatchesScheme]
  );

  const bothCount = useMemo(
    () => carts.filter((c) => cartMatchesScheme(c, 'Both')).length,
    [carts, cartMatchesScheme]
  );
  const singleCount = useMemo(
    () => carts.filter((c) => cartMatchesScheme(c, 'Single')).length,
    [carts, cartMatchesScheme]
  );
  const multiCount = useMemo(
    () => carts.filter((c) => cartMatchesScheme(c, 'Multi')).length,
    [carts, cartMatchesScheme]
  );

  const sortedShelves = useMemo(() => {
    return sortBy(shelves, shelfSortDir, (s) => {
      if (shelfSortField === 'isActive') return s.isActive;
      if (shelfSortField === 'totalSlots') return s.totalSlots || 0;
      if (shelfSortField === 'usedSlots') return s.usedSlots || 0;
      if (shelfSortField === 'shelfName') return s.shelfName || '';
      return s.shelfCode || '';
    });
  }, [shelves, shelfSortDir, shelfSortField]);

  const sortedCargoSettings = useMemo(() => {
    return sortBy(cargoSettings, cargoSortDir, (c) => {
      if (cargoSortField === 'isDefault') return c.isDefault;
      if (cargoSortField === 'isActive') return c.isActive;
      if (cargoSortField === 'hasApiKey') return c.hasApiKey;
      if (cargoSortField === 'senderName') return c.senderName || '';
      return c.provider || '';
    });
  }, [cargoSettings, cargoSortDir, cargoSortField]);

  const toggleSort = <TField extends string>(
    field: TField,
    activeField: TField,
    activeDir: SortDirection,
    setField: (value: TField) => void,
    setDir: (value: SortDirection) => void
  ) => {
    if (field === activeField) {
      setDir(activeDir === 'asc' ? 'desc' : 'asc');
      return;
    }
    setField(field);
    setDir('asc');
  };

  const summaryCards = [
    { key: 'carts', title: String(carts.length), subtitle: 'Toplama Arabası', color: STATS_COLORS.blue, icon: <ShoppingCartIcon /> },
    { key: 'shelves', title: String(shelves.length), subtitle: 'Paketleme Rafı', color: STATS_COLORS.purple, icon: <ViewModuleIcon /> },
    { key: 'cargo', title: String(cargoSettings.length), subtitle: 'Kargo Ayarı', color: STATS_COLORS.teal, icon: <LocalShippingOutlinedIcon /> },
    { key: 'active', title: String(carts.filter((c) => c.isActive).length), subtitle: 'Aktif Araba', color: STATS_COLORS.green, icon: <Inventory2Icon /> },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h5" fontWeight={700}>Ekipman Yönetimi</Typography>
          <GuideButton guideKey="wms.equipment" />
        </Box>
        <FormControl
          size="small"
          sx={{
            minWidth: 230,
            bgcolor: 'background.paper',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
          }}
        >
          <InputLabel>Depo</InputLabel>
          <Select
            value={selectedWarehouseId}
            label="Depo"
            startAdornment={<WarehouseIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />}
            onChange={e => setSelectedWarehouseId(e.target.value)}
          >
            {warehouses.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedWarehouseId && (
        <StatsCardsContainer loading={loading && (carts.length + shelves.length + cargoSettings.length) === 0}>
          {summaryCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.key}>
              <StatsCardItem icon={card.icon} color={card.color} title={card.title} subtitle={card.subtitle} />
            </Grid>
          ))}
        </StatsCardsContainer>
      )}

      {!selectedWarehouseId && (
        <Alert severity="info">Lütfen bir akıllı depo seçin</Alert>
      )}

      {selectedWarehouseId && (
        <>
          <Paper
            variant="outlined"
            sx={{
              mb: 2,
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
              bgcolor: 'background.paper',
            }}
          >
            <Tabs
              value={tab}
              onChange={(_e, v) => setTab(v)}
              variant={isMobile ? 'scrollable' : 'standard'}
              allowScrollButtonsMobile
              sx={{ px: 1.5, pt: 0.5, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label={`Toplama Arabaları (${carts.length})`} />
              <Tab label={`Paketleme Rafları (${shelves.length})`} />
              <Tab label={`Kargo Ayarları (${cargoSettings.length})`} icon={<LocalShippingIcon />} iconPosition="start" />
            </Tabs>
          </Paper>

          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

          {/* TAB 0: Carts */}
          {!loading && tab === 0 && (
            <Paper variant="outlined">
              <Tabs
                value={cartScheme}
                onChange={(_e, v) => setCartScheme(v)}
                variant={isMobile ? 'scrollable' : 'standard'}
                allowScrollButtonsMobile
                sx={{ px: 1.5, pt: 0.5, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab value="Both" label={`Her İkisi (${bothCount})`} />
                <Tab value="Single" label={`Tekli Arabalar (${singleCount})`} />
                <Tab value="Multi" label={`Çoklu Arabalar (${multiCount})`} />
              </Tabs>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {cartScheme === 'Both' ? 'Her İkisi' : cartScheme === 'Single' ? 'Tekli Arabalar' : 'Çoklu Arabalar'}
                </Typography>
                <Button size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={() => { setEditCart(null); setCartModalOpen(true); }}>
                  {cartScheme === 'Both' ? 'Araba Ekle' : cartScheme === 'Single' ? 'Tekli Araba Ekle' : 'Çoklu Araba Ekle'}
                </Button>
              </Box>
              {filteredCarts.length === 0 ? (
                <Alert severity="info" sx={{ mx: 1.5, mb: 1.5 }}>
                  {cartScheme === 'Both' ? 'Henüz araba eklenmemiş' : cartScheme === 'Single' ? 'Henüz tekli araba eklenmemiş' : 'Henüz çoklu araba eklenmemiş'}
                </Alert>
              ) : (
                isMobile ? (
                  <Box sx={{ p: 1.5, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {filteredCarts.map((cart) => (
                      <Paper key={cart.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{cart.cartCode}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{cart.cartName}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          <Chip
                            label={STATUS_LABELS[cart.status] || cart.status}
                            size="small"
                            color={STATUS_COLORS[cart.status] || 'default'}
                            sx={STATUS_CHIP_SX}
                          />
                          <Chip
                            label={cart.isActive ? 'Aktif' : 'Pasif'}
                            size="small"
                            variant="outlined"
                            color={cart.isActive ? 'success' : 'default'}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {cart.barcodeData ? (
                            <BarcodePrintButton
                              barcodeData={cart.barcodeData}
                              label={cart.cartName}
                              sublabel={cart.cartCode}
                            />
                          ) : <Box />}
                          <Box>
                            <Tooltip title="Düzenle">
                              <IconButton size="small" onClick={() => { setEditCart(cart); setCartModalOpen(true); }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sil">
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget({ type: 'cart', id: cart.id, name: cart.cartName })}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Table size="small">
                    <TableHead sx={TABLE_HEADER_SX}>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={cartSortField === 'cartCode'}
                            direction={cartSortField === 'cartCode' ? cartSortDir : 'asc'}
                            onClick={() => toggleSort('cartCode', cartSortField, cartSortDir, setCartSortField, setCartSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Kod
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={cartSortField === 'cartName'}
                            direction={cartSortField === 'cartName' ? cartSortDir : 'asc'}
                            onClick={() => toggleSort('cartName', cartSortField, cartSortDir, setCartSortField, setCartSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Ad
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={cartSortField === 'cartType'}
                            direction={cartSortField === 'cartType' ? cartSortDir : 'asc'}
                            onClick={() => toggleSort('cartType', cartSortField, cartSortDir, setCartSortField, setCartSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Tip
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={cartSortField === 'status'}
                            direction={cartSortField === 'status' ? cartSortDir : 'asc'}
                            onClick={() => toggleSort('status', cartSortField, cartSortDir, setCartSortField, setCartSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Durum
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={cartSortField === 'isActive'}
                            direction={cartSortField === 'isActive' ? cartSortDir : 'asc'}
                            onClick={() => toggleSort('isActive', cartSortField, cartSortDir, setCartSortField, setCartSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Aktif
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Barkod</TableCell>
                        <TableCell align="right">İşlem</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCarts.map(cart => (
                        <TableRow key={cart.id} hover>
                          <TableCell><Typography variant="body2" fontWeight={500}>{cart.cartCode}</Typography></TableCell>
                          <TableCell>{cart.cartName}</TableCell>
                          <TableCell>
                            <Chip label={cartTypeLabel(cart.cartType)} size="small" variant="outlined"
                              color={cart.cartType === 'Multi' ? 'secondary' : cart.cartType === 'Single' ? 'info' : 'default'} />
                          </TableCell>
                          <TableCell>
                            <Chip label={STATUS_LABELS[cart.status] || cart.status} size="small"
                              color={STATUS_COLORS[cart.status] || 'default'}
                              sx={STATUS_CHIP_SX}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label={cart.isActive ? 'Aktif' : 'Pasif'} size="small" variant="outlined"
                              color={cart.isActive ? 'success' : 'default'} />
                          </TableCell>
                          <TableCell>
                            {cart.barcodeData && (
                              <BarcodePrintButton barcodeData={cart.barcodeData}
                                label={cart.cartName} sublabel={cart.cartCode} />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Düzenle">
                              <IconButton size="small" onClick={() => {
                                setEditCart(cart);
                                setCartModalOpen(true);
                              }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sil">
                              <IconButton size="small" color="error"
                                onClick={() => setDeleteTarget({ type: 'cart', id: cart.id, name: cart.cartName })}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              )}
            </Paper>
          )}

          {/* TAB 1: Shelves */}
          {!loading && tab === 1 && (
            <Paper variant="outlined">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={600}>Paketleme Rafları</Typography>
                <Button size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={() => { setEditShelf(null); setShelfModalOpen(true); }}>
                  Raf Ekle
                </Button>
              </Box>
              {shelves.length === 0 ? (
                <Alert severity="info" sx={{ mx: 1.5, mb: 1.5 }}>Henüz paketleme rafı eklenmemiş</Alert>
              ) : (
                isMobile ? (
                  <Box sx={{ p: 1.5, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {sortedShelves.map((shelf) => (
                      <Paper key={shelf.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{shelf.shelfCode}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{shelf.shelfName}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          <Chip label={`${shelf.usedSlots}/${shelf.totalSlots} dolu`} size="small" variant="outlined" color={shelf.usedSlots > 0 ? 'warning' : 'default'} />
                          <Chip label={shelf.isActive ? 'Aktif' : 'Pasif'} size="small" variant="outlined" color={shelf.isActive ? 'success' : 'default'} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: expandedShelfId === shelf.id ? 1 : 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={expandedShelfId === shelf.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              onClick={() => handleToggleShelf(shelf.id)}
                            >
                              {expandedShelfId === shelf.id ? 'Bölmeleri Gizle' : 'Bölmeleri Gör'}
                            </Button>
                            {shelf.barcodeData && (
                              <BarcodePrintButton barcodeData={shelf.barcodeData} label={shelf.shelfName} sublabel={shelf.shelfCode} />
                            )}
                          </Box>
                          <Box>
                            <Tooltip title="Düzenle">
                              <IconButton size="small" onClick={() => {
                                setEditShelf({ ...shelf, id: shelf.id, shelfCode: shelf.shelfCode, shelfName: shelf.shelfName, totalSlots: shelf.totalSlots, isActive: shelf.isActive } as any);
                                setShelfModalOpen(true);
                              }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sil">
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget({ type: 'shelf', id: shelf.id, name: shelf.shelfName })}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Collapse in={expandedShelfId === shelf.id} timeout="auto" unmountOnExit>
                          <Box sx={{ pt: 1 }}>
                            {loadingSlots ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
                            ) : shelfSlots.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">Bölme bulunamadı</Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {shelfSlots.map((slot) => (
                                  <Paper
                                    key={slot.id}
                                    variant="outlined"
                                    sx={{ px: 1, py: 0.8, borderColor: slot.currentOrderId ? 'warning.main' : 'divider' }}
                                  >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="caption" fontWeight={700}>{slot.slotCode}</Typography>
                                      <Chip label={STATUS_LABELS[slot.status] || slot.status} size="small" color={STATUS_COLORS[slot.status] || 'default'} sx={{ height: 18, fontSize: '0.6rem' }} />
                                    </Box>
                                    <Typography variant="body2" fontSize="0.75rem" color={slot.currentOrderNumber ? 'warning.dark' : 'text.secondary'}>
                                      {slot.currentOrderNumber || 'Boş'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                                      {slot.barcodeData && <BarcodePrintButton barcodeData={slot.barcodeData} label={slot.slotCode} size="small" />}
                                      {slot.currentOrderId && (
                                        <Tooltip title="Bölmeyi Temizle">
                                          <IconButton size="small" onClick={() => handleClearSlot(slot.id)}>
                                            <ClearIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </Paper>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Table size="small">
                    <TableHead sx={TABLE_HEADER_SX}>
                      <TableRow>
                        <TableCell width={40} />
                        <TableCell>
                          <TableSortLabel
                            active={shelfSortField === 'shelfCode'}
                            direction={shelfSortField === 'shelfCode' ? shelfSortDir : 'asc'}
                            onClick={() => toggleSort('shelfCode', shelfSortField, shelfSortDir, setShelfSortField, setShelfSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Kod
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={shelfSortField === 'shelfName'}
                            direction={shelfSortField === 'shelfName' ? shelfSortDir : 'asc'}
                            onClick={() => toggleSort('shelfName', shelfSortField, shelfSortDir, setShelfSortField, setShelfSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Ad
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={shelfSortField === 'totalSlots'}
                            direction={shelfSortField === 'totalSlots' ? shelfSortDir : 'asc'}
                            onClick={() => toggleSort('totalSlots', shelfSortField, shelfSortDir, setShelfSortField, setShelfSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Bölme
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={shelfSortField === 'usedSlots'}
                            direction={shelfSortField === 'usedSlots' ? shelfSortDir : 'asc'}
                            onClick={() => toggleSort('usedSlots', shelfSortField, shelfSortDir, setShelfSortField, setShelfSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Dolu
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={shelfSortField === 'isActive'}
                            direction={shelfSortField === 'isActive' ? shelfSortDir : 'asc'}
                            onClick={() => toggleSort('isActive', shelfSortField, shelfSortDir, setShelfSortField, setShelfSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Durum
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Barkod</TableCell>
                        <TableCell align="right">İşlem</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedShelves.map(shelf => (
                        <React.Fragment key={shelf.id}>
                          <TableRow hover sx={{ cursor: 'pointer' }}
                            onClick={() => handleToggleShelf(shelf.id)}>
                            <TableCell>
                              <IconButton size="small">
                                {expandedShelfId === shelf.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell><Typography variant="body2" fontWeight={500}>{shelf.shelfCode}</Typography></TableCell>
                            <TableCell>{shelf.shelfName}</TableCell>
                            <TableCell>{shelf.totalSlots}</TableCell>
                            <TableCell>
                              <Chip label={`${shelf.usedSlots}/${shelf.totalSlots}`} size="small"
                                color={shelf.usedSlots > 0 ? 'warning' : 'default'} variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Chip label={shelf.isActive ? 'Aktif' : 'Pasif'} size="small" variant="outlined"
                                color={shelf.isActive ? 'success' : 'default'} />
                            </TableCell>
                            <TableCell onClick={e => e.stopPropagation()}>
                              {shelf.barcodeData && (
                                <BarcodePrintButton barcodeData={shelf.barcodeData}
                                  label={shelf.shelfName} sublabel={shelf.shelfCode} />
                              )}
                            </TableCell>
                            <TableCell align="right" onClick={e => e.stopPropagation()}>
                              <Tooltip title="Düzenle">
                                <IconButton size="small" onClick={() => {
                                  setEditShelf({
                                    ...shelf,
                                    id: shelf.id,
                                    shelfCode: shelf.shelfCode,
                                    shelfName: shelf.shelfName,
                                    totalSlots: shelf.totalSlots,
                                    isActive: shelf.isActive,
                                  } as any);
                                  setShelfModalOpen(true);
                                }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sil">
                                <IconButton size="small" color="error"
                                  onClick={() => setDeleteTarget({ type: 'shelf', id: shelf.id, name: shelf.shelfName })}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={8} sx={{ py: 0, px: 0, borderBottom: expandedShelfId === shelf.id ? undefined : 'none' }}>
                              <Collapse in={expandedShelfId === shelf.id} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                                  {loadingSlots ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
                                  ) : shelfSlots.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">Bölme bulunamadı</Typography>
                                  ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      {shelfSlots.map(slot => (
                                        <Paper key={slot.id} variant="outlined" sx={{
                                          px: 1.5, py: 1, minWidth: 140,
                                          borderColor: slot.currentOrderId ? 'warning.main' : 'divider',
                                          bgcolor: slot.currentOrderId ? 'warning.50' : 'background.paper',
                                        }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="caption" fontWeight={600}>{slot.slotCode}</Typography>
                                            <Chip label={STATUS_LABELS[slot.status] || slot.status} size="small"
                                              color={STATUS_COLORS[slot.status] || 'default'}
                                              sx={{ height: 18, fontSize: '0.6rem' }} />
                                          </Box>
                                          <Typography variant="body2" fontSize="0.75rem"
                                            color={slot.currentOrderNumber ? 'warning.dark' : 'text.secondary'}>
                                            {slot.currentOrderNumber || 'Boş'}
                                          </Typography>
                                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                            {slot.barcodeData && (
                                              <BarcodePrintButton barcodeData={slot.barcodeData}
                                                label={slot.slotCode} size="small" />
                                            )}
                                            {slot.currentOrderId && (
                                              <Tooltip title="Bölmeyi Temizle">
                                                <IconButton size="small" onClick={() => handleClearSlot(slot.id)}>
                                                  <ClearIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </Box>
                                        </Paper>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                )
              )}
            </Paper>
          )}

          {/* TAB 2: Cargo Settings */}
          {!loading && tab === 2 && (
            <Paper variant="outlined">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={600}>Kargo Firma Ayarları</Typography>
                <Button size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={() => { setEditCargo(null); setCargoModalOpen(true); }}>
                  Kargo Firması Ekle
                </Button>
              </Box>
              {cargoSettings.length === 0 ? (
                <Alert severity="info" sx={{ mx: 1.5, mb: 1.5 }}>Henüz kargo firması tanımlanmamış</Alert>
              ) : (
                isMobile ? (
                  <Box sx={{ p: 1.5, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {sortedCargoSettings.map((cs) => (
                      <Paper key={cs.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{cs.provider}</Typography>
                        <Typography variant="body2" color="text.secondary">{cs.senderName || '-'}</Typography>
                        {cs.senderPhone && (
                          <Typography variant="caption" color="text.secondary">{cs.senderPhone}</Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 1 }}>
                          {cs.isDefault && <Chip label="Varsayılan" size="small" color="primary" />}
                          <Chip label={cs.isActive ? 'Aktif' : 'Pasif'} size="small" variant="outlined" color={cs.isActive ? 'success' : 'default'} />
                          <Chip label={cs.hasApiKey ? 'API Tanımlı' : 'API Yok'} size="small" variant="outlined" color={cs.hasApiKey ? 'success' : 'warning'} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip title="Düzenle">
                            <IconButton size="small" onClick={() => { setEditCargo(cs); setCargoModalOpen(true); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Test Et">
                            <IconButton size="small" color="info" disabled={testingConnection === cs.id} onClick={() => handleTestConnection(cs.id)}>
                              {testingConnection === cs.id ? <CircularProgress size={16} /> : <CheckCircleIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget({ type: 'cargo', id: cs.id, name: cs.provider })}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Table size="small">
                    <TableHead sx={TABLE_HEADER_SX}>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={cargoSortField === 'provider'}
                            direction={cargoSortField === 'provider' ? cargoSortDir : 'asc'}
                            onClick={() => toggleSort('provider', cargoSortField, cargoSortDir, setCargoSortField, setCargoSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Firma
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={cargoSortField === 'isDefault'}
                            direction={cargoSortField === 'isDefault' ? cargoSortDir : 'asc'}
                            onClick={() => toggleSort('isDefault', cargoSortField, cargoSortDir, setCargoSortField, setCargoSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Varsayılan
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={cargoSortField === 'senderName'}
                            direction={cargoSortField === 'senderName' ? cargoSortDir : 'asc'}
                            onClick={() => toggleSort('senderName', cargoSortField, cargoSortDir, setCargoSortField, setCargoSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Gönderen
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={cargoSortField === 'isActive'}
                            direction={cargoSortField === 'isActive' ? cargoSortDir : 'asc'}
                            onClick={() => toggleSort('isActive', cargoSortField, cargoSortDir, setCargoSortField, setCargoSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            Durum
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={cargoSortField === 'hasApiKey'}
                            direction={cargoSortField === 'hasApiKey' ? cargoSortDir : 'asc'}
                            onClick={() => toggleSort('hasApiKey', cargoSortField, cargoSortDir, setCargoSortField, setCargoSortDir)}
                            sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                          >
                            API
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">İşlem</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedCargoSettings.map(cs => (
                        <TableRow key={cs.id} hover>
                          <TableCell><Typography variant="body2" fontWeight={500}>{cs.provider}</Typography></TableCell>
                          <TableCell>{cs.isDefault ? <Chip label="Varsayılan" size="small" color="primary" /> : '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{cs.senderName || '-'}</Typography>
                            <Typography variant="caption" color="text.secondary">{cs.senderPhone || ''}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={cs.isActive ? 'Aktif' : 'Pasif'} size="small" variant="outlined"
                              color={cs.isActive ? 'success' : 'default'} />
                          </TableCell>
                          <TableCell>
                            <Chip label={cs.hasApiKey ? 'Tanımlı' : 'Yok'} size="small" variant="outlined"
                              color={cs.hasApiKey ? 'success' : 'warning'} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Düzenle">
                              <IconButton size="small" onClick={() => { setEditCargo(cs); setCargoModalOpen(true); }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Test Et">
                              <IconButton size="small" color="info"
                                disabled={testingConnection === cs.id}
                                onClick={() => handleTestConnection(cs.id)}>
                                {testingConnection === cs.id ? <CircularProgress size={16} /> : <CheckCircleIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sil">
                              <IconButton size="small" color="error"
                                onClick={() => setDeleteTarget({ type: 'cargo', id: cs.id, name: cs.provider })}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              )}
            </Paper>
          )}
        </>
      )}

      {/* Cart Modal */}
      <CartModal
        open={cartModalOpen}
        onClose={() => setCartModalOpen(false)}
        onSave={handleSaveCart}
        defaultCartType={cartScheme === 'Both' ? '' : cartScheme}
        editData={editCart ? {
          id: editCart.id,
          cartCode: editCart.cartCode,
          cartName: editCart.cartName,
          cartType: editCart.cartType,
          status: editCart.status,
          isActive: editCart.isActive,
        } : null}
      />

      {/* Shelf Modal */}
      <ShelfModal
        open={shelfModalOpen}
        onClose={() => setShelfModalOpen(false)}
        onSave={handleSaveShelf}
        editData={editShelf ? {
          id: editShelf.id,
          shelfCode: (editShelf as any).shelfCode,
          shelfName: (editShelf as any).shelfName,
          totalSlots: (editShelf as any).totalSlots,
          isActive: (editShelf as any).isActive,
        } : null}
      />

      {/* Cargo Settings Modal */}
      <CargoSettingsModal
        open={cargoModalOpen}
        onClose={() => setCargoModalOpen(false)}
        editData={editCargo}
        warehouseId={selectedWarehouseId}
        providers={cargoProviders}
        onSave={async () => { loadData(); setCargoModalOpen(false); showSnackbar(editCargo ? 'Kargo ayarı güncellendi' : 'Kargo firması eklendi'); }}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Silme Onayı</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{deleteTarget?.name}</strong> {deleteTarget?.type === 'cart' ? 'arabasını' : deleteTarget?.type === 'shelf' ? 'rafını ve bölmelerini' : 'kargo ayarını'} silmek istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>İptal</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ==================== Cargo Settings Modal ====================

interface CargoSettingsModalProps {
  open: boolean;
  onClose: () => void;
  editData: CargoSettings | null;
  warehouseId: string;
  providers: string[];
  onSave: () => void;
}

const CargoSettingsModal: React.FC<CargoSettingsModalProps> = ({ open, onClose, editData, warehouseId, providers, onSave }) => {
  const [provider, setProvider] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [senderAddress, setSenderAddress] = useState('');
  const [senderCity, setSenderCity] = useState('');
  const [senderDistrict, setSenderDistrict] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && editData) {
      setProvider(editData.provider);
      setIsDefault(editData.isDefault);
      setApiUrl(editData.apiUrl || '');
      setApiKey('');
      setApiSecret('');
      setAccountCode(editData.accountCode || '');
      setSenderName(editData.senderName || '');
      setSenderPhone(editData.senderPhone || '');
      setSenderAddress(editData.senderAddress || '');
      setSenderCity(editData.senderCity || '');
      setSenderDistrict(editData.senderDistrict || '');
      setError('');
    } else if (open) {
      setProvider(providers[0] || '');
      setIsDefault(false);
      setApiUrl('');
      setApiKey('');
      setApiSecret('');
      setAccountCode('');
      setSenderName('');
      setSenderPhone('');
      setSenderAddress('');
      setSenderCity('');
      setSenderDistrict('');
      setError('');
    }
  }, [open, editData, providers]);

  const handleSave = async () => {
    if (!provider) { setError('Kargo firması seçin'); return; }
    setSaving(true);
    setError('');
    try {
      if (editData) {
        const dto: UpdateCargoSettingsRequest = {
          id: editData.id,
          provider,
          isDefault,
          apiUrl: apiUrl || undefined,
          apiKey: apiKey || undefined,
          apiSecret: apiSecret || undefined,
          accountCode: accountCode || undefined,
          senderName: senderName || undefined,
          senderPhone: senderPhone || undefined,
          senderAddress: senderAddress || undefined,
          senderCity: senderCity || undefined,
          senderDistrict: senderDistrict || undefined,
        };
        await cargoService.updateSettings(dto);
      } else {
        const dto: CreateCargoSettingsRequest = {
          warehouseId,
          provider,
          isDefault,
          apiUrl: apiUrl || undefined,
          apiKey: apiKey || undefined,
          apiSecret: apiSecret || undefined,
          accountCode: accountCode || undefined,
          senderName: senderName || undefined,
          senderPhone: senderPhone || undefined,
          senderAddress: senderAddress || undefined,
          senderCity: senderCity || undefined,
          senderDistrict: senderDistrict || undefined,
        };
        await cargoService.createSettings(dto);
      }
      onSave();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.Message || 'Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editData ? 'Kargo Ayarını Düzenle' : 'Kargo Firması Ekle'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth size="small">
            <InputLabel>Kargo Firması</InputLabel>
            <Select value={provider} label="Kargo Firması" onChange={e => setProvider(e.target.value)}>
              {providers.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField label="API URL" size="small" fullWidth value={apiUrl}
            onChange={e => setApiUrl(e.target.value)} placeholder="https://api.example.com" />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label={editData?.hasApiKey ? 'API Key (değiştirmek için yazın)' : 'API Key'} size="small" fullWidth
              value={apiKey} onChange={e => setApiKey(e.target.value)} type="password" />
            <TextField label={editData?.hasApiSecret ? 'API Secret (değiştirmek için yazın)' : 'API Secret'} size="small" fullWidth
              value={apiSecret} onChange={e => setApiSecret(e.target.value)} type="password" />
          </Box>

          <TextField label="Hesap/Müşteri Kodu" size="small" fullWidth value={accountCode}
            onChange={e => setAccountCode(e.target.value)} />

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Gönderen Bilgileri</Typography>

          <TextField label="Gönderen Adı" size="small" fullWidth value={senderName}
            onChange={e => setSenderName(e.target.value)} />

          <TextField label="Telefon" size="small" fullWidth value={senderPhone}
            onChange={e => setSenderPhone(e.target.value)} />

          <TextField label="Adres" size="small" fullWidth multiline rows={2} value={senderAddress}
            onChange={e => setSenderAddress(e.target.value)} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="İl" size="small" fullWidth value={senderCity}
              onChange={e => setSenderCity(e.target.value)} />
            <TextField label="İlçe" size="small" fullWidth value={senderDistrict}
              onChange={e => setSenderDistrict(e.target.value)} />
          </Box>

          <FormControlLabel
            control={<Switch checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />}
            label="Varsayılan kargo firması"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>İptal</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !provider}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}>
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WmsEquipmentManagement;
